import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { ANALYTIC_EVENTS, Analytics } from 'analytics/Analytics';
import ArrowButton from 'assets/icons/base/arrow-button.svg';
import EmailIcon from 'assets/images/email-icon.svg';
import { SEARCH_PARAMS } from 'constants/search-params';
import { useTranslate, useValidate, useYM } from 'hooks';
import { useAppSelector } from 'store/hooks';
import { useSendCheckMutation } from 'store/payment/service';
import { ButtonKinds } from 'types/button';
import { getQueryParams } from 'utils';
import { Button, Input } from 'views/components/base/ui';
import { SpinnerIcon } from 'views/shared/spinner-icon';

import './index.scss';

type EmailFormType = {
  email: '';
};

const SendCheck = () => {
  const { paymentData } = useAppSelector((state) => state.payment);
  const [check, { isLoading }] = useSendCheckMutation();
  const { control, handleSubmit, setFocus } = useForm<EmailFormType>({
    mode: 'onBlur',
    defaultValues: {
      email: '',
    },
  });
  const [isSendCheck, setIsSendCheck] = useState(false);
  const [checkSended, setCheckSended] = useState(false);
  const { validateEmail } = useValidate();
  const { t } = useTranslate();
  const { trackOnClick } = useYM();

  const openSendCheck = () => {
    trackOnClick('Отправить чек');
    setIsSendCheck(true);
  };

  useEffect(() => {
    if (isSendCheck) {
      trackOnClick('openEmailForm');
      setFocus('email');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSendCheck, setFocus]);

  const handleSendCheck = async (data: EmailFormType) => {
    trackOnClick('Отправить чек на почту');

    if (paymentData) {
      const checkData = {
        ID: paymentData.ID,
        email: data.email,
      };

      check({ ID: paymentData.ID, check: checkData })
        .unwrap()
        .then(() => {
          Analytics.sentEvent(ANALYTIC_EVENTS.checkSendSuccess, {
            client_email: checkData.email,
          });

          setCheckSended(true);
        })
        .catch((error) => {
          Analytics.sentEvent(ANALYTIC_EVENTS.checkSendError, {
            transaction_id: getQueryParams(SEARCH_PARAMS.ID),
            message: error.message,
            client_email: checkData.email,
          });
        });
    }
  };

  const renderEmailSteps = () => {
    if (checkSended) {
      return (
        <div className="text-sm leading-5 font-semibold">
          <Link
            to={{
              pathname: '/payment/viewcheck',
              search: `?id=${paymentData?.ID}`,
            }}
            className="font-bold underline hover:no-underline cursor-pointer"
          >
            {t('lang.check')}
          </Link>{' '}
          {t('lang.check_email_for_receipt')}
        </div>
      );
    }

    if (isSendCheck) {
      return (
        <form onSubmit={handleSubmit(handleSendCheck)} className="flex items-center gap-4">
          <Controller
            name="email"
            control={control}
            rules={{ required: true, validate: validateEmail }}
            render={({ field: { value, onChange, onBlur, ref }, fieldState: { error } }) => (
              <Input
                ref={ref}
                onBlur={onBlur}
                name="email"
                placeholder={t('enter_email_base')}
                value={value}
                containerClassName="w-full"
                inputClassName="text-base border-white/60 none-bg-autofill white-autofill-text bg-[transparent] hover:border-white focus:border-white text-white placeholder:text-white/60"
                onChange={onChange}
                error={error?.message}
              />
            )}
          />
          <button
            type="button"
            className="p-2 rounded-xsmall hover:bg-white/10 active:bg-white/20"
            onClick={handleSubmit(handleSendCheck)}
          >
            {isLoading ? (
              <SpinnerIcon />
            ) : (
              <img src={ArrowButton} className="w-6 h-6 md:w-8 md:h-8" alt="send check" />
            )}
          </button>
        </form>
      );
    }

    return (
      <Button
        kind={ButtonKinds.success}
        className="flex justify-center items-center gap-3"
        onClick={openSendCheck}
      >
        <img src={EmailIcon} width={16} height={16} alt="email" />
        {t('lang.send_receipt_to_email')}
      </Button>
    );
  };

  return <div className="md:max-w-[270px]">{renderEmailSteps()}</div>;
};

export { SendCheck };
